import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import {
	useAuthUser,
	useJobRunsList,
	useJobTasksList,
	useQuestionsEnabled,
	useSuspenseFeatureAccess,
	useSuspenseJob,
	useUpdateJob,
} from '../../api';
import { useCatalogList } from '../../api/hooks/resourceCatalog';
import Documentation from '../../components/Documentation';
import EntityDiscussions from '../../components/EntityDiscussions';
import { getEntityPageSidebarProps } from '../../components/EntityDrawer/utils';
import EntityLogo from '../../components/EntityLogo/EntityLogo';
import EntityPageLayout from '../../components/EntityPageLayout';
import EntityPageLayoutSkeleton from '../../components/EntityPageLayout/EntityPageLayoutSkeleton';
import { useLineageTab } from '../../components/EntityPageLayout/hooks';
import {
	getDefaultTab,
	getOrderedTabs,
} from '../../components/EntityPageLayout/utils';
import { LineageTab } from '../../components/LineageGraph/LineageTab';
import type { OverviewPageTab } from '../../components/Overview/OverviewPageTabs';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs';
import { useTabNavigate } from '../../utils/hook/useTabNavigate';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import type { DjangoValueType } from '../TemplatePage/types';
import RunTable from './components/RunTable';
import TablesTab from './components/TablesTab';
import TaskTable from './components/TaskTable';

const InnerJobPage = observer(({ id }: { id: string }) => {
	const { user, workspace } = useAuthUser();
	const navigateToTab = useTabNavigate();
	const { lineageAccess, secodaCoreAccess } = useSuspenseFeatureAccess();
	const questionsEnabled = useQuestionsEnabled();
	const { data: job } = useSuspenseJob({
		id,
	});

	useMount(() => {
		trackEvent('job/open', {}, user, workspace);
	});

	const { mutateAsync } = useUpdateJob({});

	const updateJob = useCallback(
		async (key: string, value: DjangoValueType, saveRemotely = true) => {
			if (saveRemotely) {
				await mutateAsync({
					data: {
						id: job!.id,
						[key]: value,
					},
				});
				trackEvent('job/properties/update', {}, user, workspace!);
			}
		},
		[mutateAsync, job, user, workspace]
	);

	if (!job) {
		return <EntityPageLayoutSkeleton />;
	}

	const { data: jobRuns, isLoading: isRunLoading } = useJobRunsList({
		filters: {
			parent_id: job.id,
			sort_by: '-run_finished_at',
		},
		options: {
			select: (res) => res.results,
		},
	});

	const { data: jobTasks, isLoading: isTaskLoading } = useJobTasksList({
		filters: {
			parent_id: job.id,
		},
		options: {
			select: (res) => res.results,
		},
	});

	const { data: jobTables, isLoading: isTablesLoading } = useCatalogList({
		filters: {
			entity_type: EntityType.table,
			filter: {
				operator: 'and',
				operands: [
					{
						operands: [],
						field: 'parent_id',
						operator: 'exact',
						value: job.id,
					},
				],
			},
			page_size: 1, // We only need to check if any exist
		},
		options: {
			select: (res) => res.results,
		},
	});

	const documentationFirst = (job.definition?.length || 0) > 2;

	const handleTabChange = useCallback(
		(updatedTab: string) => {
			if (updatedTab === 'lineage') {
				trackEvent('job/lineage/open', {}, user, workspace);
			}
			navigateToTab(updatedTab);
		},
		[navigateToTab, user, workspace]
	);

	const { lineageFlowStore, lineageFlowUIStore, lineageFilterStore } =
		useLineageTab(job.id, job.published, EntityType.job, job.native_type);

	const tabs: OverviewPageTab[] = useMemo(
		() => [
			{
				value: 'lineage',
				label: 'Lineage',
				condition: !!lineageAccess,
				component: (
					<LineageTab
						lineageFlowStore={lineageFlowStore}
						lineageFlowUIStore={lineageFlowUIStore}
						lineageFilterStore={lineageFilterStore}
					/>
				),
			},
			{
				value: 'tables',
				label: 'Tables',
				condition: !isTablesLoading && jobTables && jobTables.length > 0,
				component: <TablesTab jobId={job.id} />,
			},
			{
				value: 'runs',
				label: 'Runs',
				condition: !isRunLoading && jobRuns && jobRuns.length > 0,
				component: <RunTable job={job} />,
			},
			{
				value: 'tasks',
				label: 'Tasks',
				condition: !isTaskLoading && jobTasks && jobTasks.length > 0,
				component: <TaskTable job={job} />,
			},
			{
				value: 'discussions',
				label: 'Questions',
				condition: !!secodaCoreAccess && questionsEnabled,
				component: <EntityDiscussions entity={job} />,
			},
			{
				value: 'documentation',
				label: 'Documentation',
				component: <Documentation entity={job} />,
			},
		],
		[
			job,
			isRunLoading,
			isTaskLoading,
			isTablesLoading,
			jobRuns,
			jobTasks,
			jobTables,
			lineageAccess,
			secodaCoreAccess,
			questionsEnabled,
		]
	);

	const orderedTabs: OverviewPageTab[] = useMemo(
		() => getOrderedTabs(tabs, documentationFirst),
		[tabs, documentationFirst]
	);

	const { tab: urlTabParam } = useParams();

	const defaultTab = useMemo(() => getDefaultTab(orderedTabs), [orderedTabs]);
	const currentTab = urlTabParam ?? defaultTab;

	return (
		<EntityPageLayout
			key={job.id}
			name="Job"
			isReadOnlyTitle
			entity={job}
			icon={<EntityLogo size={32} entity={job} />}
			updateEntity={updateJob}
			withDescription
			{...getEntityPageSidebarProps(EntityType.job)}
		>
			<OverviewPageTabs
				key={job.id}
				defaultTab={defaultTab}
				currentTab={currentTab}
				tabs={orderedTabs}
				onTabChange={handleTabChange}
			/>
		</EntityPageLayout>
	);
});

// By memoizing the inner component, we can avoid re-rendering the entire page on tab changes and only re-render the tab panel
export default function JobPage() {
	const id = useParamsIdSuffixUuid();

	return <InnerJobPage id={id} />;
}
